import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { images, stacks, slideNames } from "./jsonData/RecentProjectsData";

export default function RecentProjects() {
  const [activeId, setActiveId] = useState(slideNames[0].id);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = width < 900;

  // stacks per project (used for both desktop + mobile)
  const stacksMap = {
    adOnVerseApp: ["next", "js", "kotlin"],
    adOnVerse: ["next", "js", "css"],
    hamlegal: ["wordpress"],
    CasperWeb: ["html", "css"],
    DesignVerse: ["html", "css"],
    TodoApp: ["react", "css", "js"],
    PrayerTimes: ["react", "css", "js"],
    GuessWordGame: ["html", "css", "js"],
    weatherApp: ["react", "css", "redux", "js"],
    ShopClothes: ["react", "css", "redux", "js"],
    Yummy: ["css", "next", "js"],
    "cosmic-fish": ["next", "js", "css"],
    LearnFlow: ["react", "js", "css"],
  };

  const activeStacks = stacksMap[activeId] || [];

  return (
    <div
      style={{
        background: "#fff",
        color: "#111",
        padding: isMobile ? "60px 16px" : "100px 60px",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "50px" }}>
        <h5 style={{ opacity: 0.6, letterSpacing: "2px" }}>RECENT PROJECTS</h5>
        <hr style={{ borderColor: "#e5e5e5", marginTop: "16px" }} />
      </div>

      {/* ================= MOBILE ================= */}
      {isMobile && (
        <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
          {slideNames.map((project, index) => {
            const image = images.find((img) => img.id === project.id)?.image;

            return (
              <motion.a
                key={project.id}
                href={project.link}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.06 }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div
                  style={{
                    borderRadius: "18px",
                    overflow: "hidden",
                    background: "#f7f8fa",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Image */}
                  <div
                    style={{
                      height: "220px",
                      backgroundImage: `url(${image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />

                  {/* Content */}
                  <div style={{ padding: "20px" }}>
                    <h2 style={{ fontSize: "1.6rem", marginBottom: "6px" }}>
                      {project.title}
                    </h2>

                    <p style={{ fontSize: "0.9rem", color: "#666" }}>
                      Design & Development
                    </p>

                    {/* stacks (VISIBLE ON MOBILE) */}
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "14px",
                        flexWrap: "wrap",
                      }}
                    >
                      {stacks
                        .filter((s) => stacksMap[project.id]?.includes(s.id))
                        .map((stack) => (
                          <img
                            key={stack.id}
                            src={stack.icon}
                            alt={stack.id}
                            loading="lazy"
                            style={{ width: "40px", opacity: 0.9 }}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      )}

      {/* ================= DESKTOP ================= */}
      {!isMobile && (
        <div style={{ display: "flex", gap: "10px" }}>
          {/* List */}
          <div style={{ flex: 1 }}>
            {slideNames.map((project) => (
              <div
                key={project.id}
                onMouseEnter={() => setActiveId(project.id)}
                style={{
                  padding: "40px 0",
                  borderBottom: "1px solid #e5e5e5",
                  cursor: "pointer",
                }}
              >
                <h1
                  style={{
                    fontSize: "clamp(2rem, 5vw, 3.5rem) ",
                    opacity: activeId === project.id ? 1 : 0.35,
                    transition: "0.3s",
                    fontWeight: "lighter",
                  }}
                >
                  {project.title}
                </h1>
              </div>
            ))}
          </div>

          {/* Preview + STACKS (STAYS) */}
          <div
            style={{
              width: "600px",
              position: "sticky",
              top: "140px",
              height: "540px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            {/* stacks */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "14px",
                flexWrap: "wrap",
              }}
            >
              {stacks.map((stack) => (
                <img
                  key={stack.id}
                  src={stack.icon}
                  alt={stack.id}
                  style={{
                    width: "50px",
                    height: "55px",
                    filter: activeStacks.includes(stack.id)
                      ? "grayscale(0)"
                      : "grayscale(100%)",
                    opacity: activeStacks.includes(stack.id) ? 1 : 0.25,
                    transition: "0.4s",
                    marginTop: activeStacks.includes(stack.id)
                      ? "-10px"
                      : "0px",
                  }}
                />
              ))}
            </div>

            {/* image */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeId}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  flex: 1,
                  borderRadius: "16px",
                  backgroundImage: `url(${
                    images.find((img) => img.id === activeId)?.image
                  })`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* CTA */}
      <div style={{ marginTop: "90px", textAlign: "center" }}>
        <Link to="/work" style={{ textDecoration: "none" }}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            style={{
              padding: "18px 42px",
              borderRadius: "40px",
              border: "1px solid #111",
              background: "transparent",
              color: "#111",
              fontSize: "1.1rem",
            }}
          >
            View More Projects
          </motion.button>
        </Link>
      </div>
    </div>
  );
}
